import { createTheme, alpha } from '@mui/material/styles';

/** Основной шрифт интерфейса (кириллица + латиница, загрузка через index.html, display=swap) */
export const FONT_STACK = '"Manrope", system-ui, -apple-system, "Segoe UI", sans-serif';

// Общие цвета для светлой и темной темы
const commonColors = {
  success: {
    main: '#4caf50',
    light: '#81c784',
    dark: '#388e3c',
  },
  warning: {
    main: '#ff9800',
    light: '#ffb74d',
    dark: '#f57c00',
  },
  error: {
    main: '#f44336',
    light: '#e57373',
    dark: '#d32f2f',
  },
  info: {
    main: '#2196f3',
    light: '#64b5f6',
    dark: '#1976d2',
  },
};

// Создаем функцию для генерации legacyStyles
const createLegacyStyles = (mode) => ({
  MuiContainer: {
    styleOverrides: {
      root: {
        '&.container-fluid': {
          maxWidth: 'none',
          margin: '0 auto',
          padding: '12px',
          '@media (min-width: 600px)': {
            padding: '16px',
          },
          '@media (min-width: 900px)': {
            padding: '20px',
          },
        },
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:nth-of-type(odd)': {
          backgroundColor: mode === 'light' 
            ? alpha('#000', 0.02)
            : alpha('#fff', 0.02),
        },
        '&:hover': {
          backgroundColor: mode === 'light'
            ? alpha('#000', 0.04)
            : alpha('#fff', 0.04),
        },
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        marginBottom: '0.5rem',
        fontWeight: 500,
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        '&.form-control': {
          display: 'block',
          width: '100%',
          padding: '0.375rem 0.75rem',
          fontSize: '1rem',
          fontWeight: 400,
          lineHeight: 1.5,
          color: mode === 'light' ? '#212529' : '#fff',
          backgroundColor: 'transparent',
          backgroundClip: 'padding-box',
          border: `1px solid ${mode === 'light' ? '#ced4da' : '#495057'}`,
          borderRadius: '0.25rem',
          transition: 'border-color .15s ease-in-out,box-shadow .15s ease-in-out',
        },
      },
    },
  },
  MuiBox: {
    styleOverrides: {
      root: {
        '&.app': {
          paddingTop: 64, // headerHeight
          minHeight: '100vh',
          backgroundColor: mode === 'light' ? '#f5f5f5' : '#121212',
        },
      },
    },
  },
  MuiGrid: {
    styleOverrides: {
      root: {
        '&.row-fluid': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: '16px',
          '@media (min-width: 900px)': {
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: '30px',
          },
        },
        '&.span2': {
          flex: '1 1 auto',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          '@media (min-width: 900px)': {
            flex: '0 0 220px',
            width: 'auto',
            maxWidth: 'none',
          },
        },
        '&.span10': {
          flex: 1,
          minWidth: 0,
          width: '100%',
        },
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: {
        '&.page-header': {
          marginBottom: '30px',
          paddingBottom: '15px',
          borderBottom: `1px solid ${mode === 'light' ? '#e5e5e5' : '#2a2a2a'}`,
          '& h1': {
            margin: 0,
            fontSize: '1.5rem',
            fontWeight: 400,
            lineHeight: 1.3,
            color: mode === 'light' ? '#000000' : '#ffffff',
          },
        },
      },
    },
  },
});

// Создаем функцию для генерации темы
const createAppTheme = (mode = 'light') => {
  return createTheme({
    palette: {
      mode,
      ...(mode === 'light'
        ? {
            // Светлая тема
            primary: {
              main: '#1976d2',
              light: '#42a5f5',
              dark: '#1565c0',
            },
            secondary: {
              main: '#9c27b0',
              light: '#ba68c8',
              dark: '#7b1fa2',
            },
            background: {
              default: '#f5f5f5',
              paper: '#ffffff',
            },
            // Высокий контраст: основной текст чёрный; «вторичный» — тёмно-серый (не «бледный»)
            text: {
              primary: '#000000',
              secondary: '#1a1a1a',
              disabled: 'rgba(0, 0, 0, 0.38)',
            },
            ...commonColors,
          }
        : {
            // Темная тема
            primary: {
              main: '#90caf9',
              light: '#e3f2fd',
              dark: '#42a5f5',
            },
            secondary: {
              main: '#ce93d8',
              light: '#f3e5f5',
              dark: '#ab47bc',
            },
            background: {
              default: '#121212',
              paper: '#1e1e1e',
            },
            text: {
              primary: '#ffffff',
              secondary: 'rgba(255, 255, 255, 0.9)',
              disabled: 'rgba(255, 255, 255, 0.38)',
            },
            ...commonColors,
          }),
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            fontSize: '16px',
          },
          body: {
            fontFamily: FONT_STACK,
            WebkitFontSmoothing: 'auto',
            MozOsxFontSmoothing: 'auto',
            textRendering: 'optimizeLegibility',
            scrollbarColor: '#6b6b6b #2b2b2b',
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              width: 8,
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              borderRadius: 8,
              backgroundColor: mode === 'light' ? '#959595' : '#6b6b6b',
              minHeight: 24,
            },
            '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
              borderRadius: 8,
              backgroundColor: mode === 'light' ? '#f1f1f1' : '#2b2b2b',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '8px 16px',
            '&.status-cell': {
              width: 120,
            },
          },
          head: {
            fontWeight: 600,
            backgroundColor: mode === 'light' 
              ? alpha('#000', 0.02)
              : alpha('#fff', 0.05),
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            '&.form-group': {
              marginBottom: '1rem',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      // Добавляем стили из старой версии
      ...createLegacyStyles(mode),
    },
    shape: {
      borderRadius: 8,
    },
    typography: {
      fontFamily: FONT_STACK,
      fontSize: 16,
      fontWeightLight: 400,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: {
        fontSize: '2.25rem',
        fontWeight: 700,
        lineHeight: 1.2,
        /* без отрицательного трекинга: на кириллице иначе буквы визуально «плывут» */
        letterSpacing: 0,
      },
      h2: {
        fontSize: '1.875rem',
        fontWeight: 700,
        lineHeight: 1.25,
        letterSpacing: 0,
      },
      h3: {
        fontSize: '1.625rem',
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.35,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.45,
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.55,
      },
      caption: {
        fontSize: '0.75rem',
        fontWeight: 500,
        lineHeight: 1.5,
        letterSpacing: '0.02em',
      },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1.5,
        letterSpacing: '0.06em',
      },
      button: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.5,
        textTransform: 'none',
      },
    },
    // Добавляем кастомные переменные
    custom: {
      headerHeight: 64,
      sidebarWidth: 240,
      contentMaxWidth: 1200,
    },
  });
};

export default createAppTheme; 